import React, { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useForm } from 'react-hook-form';

import { useLocale, useTranslations } from 'next-intl';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@shadcn/components/ui/form';
import { FormInput } from '@shadcn/components/ui/input';
import { Textarea } from '@shadcn/components/ui/textarea';
import { Button } from '../Button';
import { Course, Courseversion, Lead } from '@/payload/payload-types';
import { Checkbox } from '@shadcn/components/ui/checkbox';
import ChevronRight from '../../_icons/Chevron-right';
import Plus from '../../_icons/Plus';
import { Link } from '@/navigation';
import { getDiscountReduction, useTrackEvent } from '../../_providers/TrackEventProvider';

export type ContactUsFormParams = {
	course: Course;
	hasSubmittedForm: boolean;
	isCourseInCart: boolean;
	addCourseToCart: () => void;
	onFormSubmit: () => void;
	selectedCourseVersion?: Courseversion | null;
};

export const ContactUsForm: React.FC<ContactUsFormParams> = ({
	course,
	hasSubmittedForm,
	onFormSubmit,
	isCourseInCart,
	addCourseToCart,
	selectedCourseVersion,
}) => {
	const t = useTranslations('Course');
	const [globalError, setGlobalError] = useState<null | string>(null);
	const [isSubmitDisabled, setSubmitDisabled] = useState<boolean>(false);

	const locale = useLocale();
	const trackEvent = useTrackEvent();

	const formSchema = z.object({
		firstName: z.string().min(1, {
			message: t('first-name-required'),
		}),
		lastName: z.string().min(1, {
			message: t('last-name-required'),
		}),
		email: z.coerce
			.string()
			.min(1, {
				message: t('email-required'),
			})
			.email({
				message: t('invalid-email'),
			}),
		phone: z.string(),
		message: z
			.string()
			.min(1, {
				message: t('message-required'),
			})
			.max(500, { message: t('message-too-long', { charCount: 500 }) }),
		acceptTerms: z.literal(true, {
			errorMap: () => ({ message: t('terms-required') }),
		}),
	});
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
			message: '',
		},
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		setSubmitDisabled(true);
		if (typeof course.courseSalesRep === 'string') {
			throw new Error(
				`Bad depth for course sales representative at ContactUsForm: ${course.title}`,
			);
		}
		try {
			const res = await fetch(
				`${process.env.NEXT_PUBLIC_SERVER_URL}/api/leads?locale=${locale}`,
				{
					body: JSON.stringify({
						interestedCourse: course.id,
						salesRep: course.courseSalesRep?.id,
						...values,
					}),
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					method: 'POST',
				},
			);

			if (res.ok) {
				const { doc, errors } = await res.json();
				if (errors) throw new Error(errors[0].message);
				const lead: Lead = doc;

				if (lead) {
					onFormSubmit();
				}
			} else {
				return res.json().then(json => {
					throw json;
				});
			}
		} catch (err) {
			// @ts-expect-error TODO error typings
			if (Array.isArray(err?.errors)) {
				// @ts-expect-error TODO error typings
				err?.errors.forEach(error => {
					if (Array.isArray(error?.data)) {
						// @ts-expect-error TODO error typings
						error?.data.forEach(({ field, message }) => {
							form.setError(field, {
								message,
							});
						});
					} else {
						if (error?.message) {
							setGlobalError(error.message);
						} else {
							setGlobalError(t('contact-form-unexpected-error'));
						}
					}
				});
			} else {
				setGlobalError(t('contact-form-unexpected-error'));
			}
		}
		setSubmitDisabled(false);
	}

	return (
		<div>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)}>
					<FormField
						control={form.control}
						name="firstName"
						render={({ field }) => (
							<FormItem className="mb-1">
								<FormLabel aria-required>{t('first-name')}</FormLabel>
								<FormControl>
									<FormInput type="text" {...field} />
								</FormControl>

								<FormMessage>&nbsp;</FormMessage>
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="lastName"
						render={({ field }) => (
							<FormItem className="mb-1">
								<FormLabel aria-required>{t('last-name')}</FormLabel>
								<FormControl>
									<FormInput type="text" {...field} />
								</FormControl>

								<FormMessage>&nbsp;</FormMessage>
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem className="mb-1">
								<FormLabel aria-required>{t('email')}</FormLabel>
								<FormControl>
									<FormInput type="email" {...field} />
								</FormControl>

								<FormMessage>&nbsp;</FormMessage>
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="phone"
						render={({ field }) => (
							<FormItem className="mb-1">
								<FormLabel>{t('phone')}</FormLabel>
								<FormControl>
									<FormInput type="phone" {...field} />
								</FormControl>

								<FormMessage>&nbsp;</FormMessage>
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="message"
						render={({ field }) => (
							<FormItem className="mb-1">
								<FormLabel aria-required>{t('message')}</FormLabel>
								<FormControl>
									<Textarea className="h-40 max-w-96" {...field} />
								</FormControl>

								<FormMessage>&nbsp;</FormMessage>
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="acceptTerms"
						render={({ field }) => (
							<FormItem className="space-y-0">
								<div className="flex flex-row items-center">
									<FormControl>
										<Checkbox
											checked={field.value}
											className="bg-white"
											onCheckedChange={field.onChange}
										/>
									</FormControl>
									<FormLabel className="text-base font-normal">
										{t('terms-of-making-inqueries')}
									</FormLabel>
								</div>

								<FormMessage>&nbsp;</FormMessage>
							</FormItem>
						)}
					/>

					{!hasSubmittedForm && (
						<div className="flex flex-col space-y-2 lg:mt-8 lg:flex-row lg:space-y-0 lg:space-x-4">
							<Button
								onClick={() => {
									trackEvent('contact_us_button_clicked', {
										course_name: course.title,
										course_discount_value_eur:
											getDiscountReduction(selectedCourseVersion),
									});
								}}
								data-fullstory="contact-form-send-button"
								size="small"
								type="submit"
								disabled={isSubmitDisabled}
							>
								{t('send')}
								<ChevronRight />
							</Button>
							{isCourseInCart ? (
								<Link
									className="group bg-success flex w-fit items-center space-x-4 rounded-lg px-4 py-2 text-sm font-bold text-white lg:text-base"
									href="/cart"
								>
									<div className="flex flex-col lg:flex-row">
										<p>{t('course-is-in-cart')}</p>

										<p className="group-hover:underline lg:ml-1">
											{t('continue-to-payment')}
										</p>
									</div>
									<ChevronRight color="white" />
								</Link>
							) : (
								<Button
									onClick={() => {
										addCourseToCart();
										trackEvent('add_to_cart_clicked', {
											course_name: course.title,
											component: 'contact_us_form',
											course_discount_value_eur:
												getDiscountReduction(selectedCourseVersion),
										});
									}}
									type="button"
									size="small"
									variant="outlined"
									intent="primary"
									disabled={isSubmitDisabled}
								>
									{t('buy-course-now')}
									<Plus color="#041E41" className="size-4" />
								</Button>
							)}
						</div>
					)}

					{globalError ? (
						<p className="text-error">{globalError}</p>
					) : (
						<p className="text-error">&nbsp;</p>
					)}
				</form>
			</Form>
		</div>
	);
};
